<template>
  <div class="ratio-carousel-wrapper" ref="container">
    <el-carousel
      :interval="interval"
      :autoplay="autoplay"
      :arrow="arrow"
      :indicator-position="indicatorPosition"
      :height="carouselHeight"
    >
      <el-carousel-item v-for="(item, index) in displayItems" :key="index">
        <div class="carousel-img-wrapper">
          <img :src="item.src" :alt="item.alt || ''" />
          <span v-if="item.label" class="carousel-label">{{ item.label }}</span>
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
export default {
  name: "RatioCarousel",
  props: {
    // Web items
    items: {
      type: Array,
      required: true,
    },
    // Mobile items
    mobileItems: {
      type: Array,
      default: () => [],
    },
    // Web aspect ratio, e.g., "16/9"
    ratio: {
      type: String,
      default: "16/9",
    },
    // Mobile aspect ratio, e.g., "3/4"
    mobileRatio: {
      type: String,
      default: "3/4",
    },
    // Mobile breakpoint
    mobileBreakpoint: {
      type: Number,
      default: 768,
    },
    // Carousel options
    interval: {
      type: Number,
      default: 5000,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    arrow: {
      type: String,
      default: "hover",
    },
    indicatorPosition: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : 1200,
      containerWidth: 0,
    };
  },
  computed: {
    isMobile() {
      return this.viewportWidth <= this.mobileBreakpoint;
    },
    // Calculate current ratio value
    currentRatioValue() {
      const ratioStr =
        this.isMobile && this.mobileItems.length > 0
          ? this.mobileRatio
          : this.ratio;
      const parts = ratioStr.split("/");
      if (parts.length === 2) {
        const w = parseFloat(parts[0]);
        const h = parseFloat(parts[1]);
        if (w && h) {
          return w / h;
        }
      }
      return 16 / 9;
    },
    // Calculate precise pixel height based on container width and ratio
    carouselHeight() {
      if (!this.containerWidth) return "300px"; // Default fallback
      const height = this.containerWidth / this.currentRatioValue;
      return height + "px";
    },
    displayItems() {
      const useMobileList =
        this.isMobile && this.mobileItems && this.mobileItems.length;
      const sourceItems = useMobileList ? this.mobileItems : this.items;

      return sourceItems.map((item) => {
        if (this.isMobile && !useMobileList && item.mobileSrc) {
          return { ...item, src: item.mobileSrc };
        }
        return item;
      });
    },
  },
  mounted() {
    this.updateDimensions();
    window.addEventListener("resize", this.updateDimensions);
    // Extra check in case of layout shifts
    this.$nextTick(() => {
      this.updateDimensions();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDimensions);
  },
  methods: {
    updateDimensions() {
      this.viewportWidth = window.innerWidth;
      if (this.$refs.container) {
        this.containerWidth = this.$refs.container.clientWidth;
      }
    },
  },
};
</script>

<style scoped>
.ratio-carousel-wrapper {
  width: 100%;
  overflow: hidden; /* Prevent any accidental overflow */
}

.carousel-img-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-img-wrapper img {
  width: 100%;
  height: 100%;
  /* object-fit: cover; */
  display: block;
}

.carousel-label {
  position: absolute;
  left: 50%;
  bottom: 8%;
  transform: translateX(-50%);
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

@media (max-width: 768px) {
  .carousel-label {
    font-size: 14px;
    bottom: 6%;
  }
}
</style>
